import { BAR_CHART_COLOR_CODES } from '@engine/utils/colorCodes'

const FONT_SIZE_12 = '12'
//  const FONT_FAMILY_MONTSERRAT = '\'Montserrat\', sans-serif'
const FONT_FAMILY_MONTSERRAT = 'DINPro-Light'
let minPoint
let maxPoint
const maxTicks = 14
let tickSpacing
let range
let niceMin
let niceMax

const float2dollar = (value, pageTitle) => {
  const pageTitleFlag = pageTitle && pageTitle.replace(/ /g, '')
  let data = null

  if (
    pageTitleFlag === 'AverageRent/SFSummary' ||
    pageTitleFlag === 'Price/SF' ||
    pageTitleFlag === 'CAP' ||
    pageTitleFlag.includes('AvgRent/SF')
  ) {
    data = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  } else {
    data = value.toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  if (pageTitleFlag === 'CAP') {
    return `${data}%`
  }

  return `$${data}`
}

const getDecimalPlaces = value => {
  const valueString = String(value)
  const decimalIndex = valueString.indexOf('.')

  return decimalIndex === -1 ? 0 : valueString.length - decimalIndex - 1
}

const buildYAxisTicks = (min, max, interval) => {
  if (
    min === undefined ||
    min === null ||
    max === undefined ||
    max === null ||
    interval === undefined ||
    interval === null ||
    interval <= 0 ||
    max < min
  ) {
    return []
  }

  const decimalPlaces = Math.max(
    getDecimalPlaces(min),
    getDecimalPlaces(max),
    getDecimalPlaces(interval)
  )
  const tickCount = Math.round((max - min) / interval)
  const ticks = []

  for (let index = 0; index <= tickCount; index += 1) {
    ticks.push(Number((min + (index * interval)).toFixed(decimalPlaces)))
  }

  return ticks
}

const animateBarChart = (chartRef, pageTitle) => {
  const { chartInstance } = chartRef
  const { ctx } = chartInstance
  ctx.font = `${FONT_SIZE_12}px ${FONT_FAMILY_MONTSERRAT}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  chartInstance.data.datasets.forEach((dataset, i) => {
    const meta = chartInstance.controller.getDatasetMeta(i)
    meta.data.forEach((bar, index) => {
      const data = Number(dataset.data[index].toFixed(2))
      ctx.save()
      const width = 40
      /* eslint no-underscore-dangle: ['error', { 'allow': ['_model'] }]*/
      const { x, y } = bar._model
      ctx.fillStyle = '#fff'
      ctx.fillRect(x - 20, y - 17, width, parseInt(ctx.font, 10))
      ctx.fillStyle = '#000'
      ctx.fillText(float2dollar(data, pageTitle), x, y - 5)
      ctx.restore()
    })
  })
}

export const customizeChartOptions = (
  defaultChartOptions,
  chartRef,
  chartConfig
) => {
  const {
    isGrid,
    yaxisConfig,
    propertyNameOrientation,
    pageTitle,
    isValueOrientation,
    barWidth = 40
  } = chartConfig
  const yAxisTicks =
    yaxisConfig &&
    buildYAxisTicks(yaxisConfig.min, yaxisConfig.max, yaxisConfig.interval)
  const hasCustomYAxisTicks = yAxisTicks && yAxisTicks.length > 0
  const yAxisConfig = {
    display: true,
    gridLines: {
      display: isGrid === 'show',
      color: '#828282',
      lineWidth: 0.5
    },
    ticks: {
      beginAtZero: false,
      min: yaxisConfig && yaxisConfig.min,
      max: yaxisConfig && yaxisConfig.max,
      stepSize: yaxisConfig && yaxisConfig.interval,
      autoSkip: hasCustomYAxisTicks ? false : undefined,
      precision:
        yaxisConfig && yaxisConfig.interval
          ? getDecimalPlaces(yaxisConfig.interval)
          : undefined,
      fontSize: `${FONT_SIZE_12}`,
      fontColor: '#000',
      fontFamily: `${FONT_FAMILY_MONTSERRAT}`,
      callback: value => float2dollar(value, pageTitle)
    }
  }

  if (hasCustomYAxisTicks) {
    yAxisConfig.afterBuildTicks = () => yAxisTicks
  }

  const scales = {
    xAxes: [
      {
        maxBarThickness: barWidth,
        display: true,
        gridLines: {
          display: false
        },
        ticks: {
          beginAtZero: true,
          fontSize: `${FONT_SIZE_12}`,
          fontColor: '#000',
          fontFamily: FONT_FAMILY_MONTSERRAT
        }
      }
    ],
    yAxes: [yAxisConfig]
  }

  if (propertyNameOrientation === 'tilt') {
    scales.xAxes[0].ticks.minRotation = 15
    scales.xAxes[0].ticks.maxRotation = 15
  }

  const newChartOptions = Object.assign({}, defaultChartOptions)
  if (isValueOrientation === 'show') {
    const animation = {
      onComplete: () => animateBarChart(chartRef.current, pageTitle)
    }
    newChartOptions.animation = animation
  }
  newChartOptions.scales = scales

  return newChartOptions
}

const isOverlapping = (
  wtdAverageValue,
  barChartSettings,
  pageTitle,
  chartData
) => {
  const noOfBars = chartData.datasets[0].data.length
  const barChartType = barChartSettings.barChartLayoutOptions?.find(
    obj => obj.pageTitle === pageTitle
  )
  const layoutType = barChartType?.layout || 1
  const firstBarValue = chartData.datasets[0]?.data[0]
  const secondBarValue = chartData.datasets[0]?.data[1]
  const thirdBarValue = chartData.datasets[0]?.data[2]
  const yaxisInterval = chartData.yaxisConfig.interval
  let isSecondBar
  let isThirdBar
  let comparedBarValue = firstBarValue
  if (layoutType === 1) {
    isThirdBar =
      thirdBarValue <= (wtdAverageValue + (yaxisInterval / 4)) && noOfBars > 16
    isSecondBar =
      secondBarValue <= (wtdAverageValue + (yaxisInterval / 4)) && noOfBars > 9
  } else if (layoutType === 2) {
    isThirdBar =
      thirdBarValue <= (wtdAverageValue + (yaxisInterval / 4)) && noOfBars > 7
    isSecondBar =
      secondBarValue <= (wtdAverageValue + (yaxisInterval / 4)) && noOfBars > 5
  }
  if (isThirdBar) {
    comparedBarValue = thirdBarValue
  } else if (isSecondBar) {
    comparedBarValue = secondBarValue
  }
  const diffWithAvgLine = Number(wtdAverageValue) - Number(comparedBarValue)

  return Math.abs(diffWithAvgLine) <= yaxisInterval / 4
}

export const prepareChartOptions = (
  wtdAverageValue,
  barChartSettings,
  pageTitle,
  chartData
) => {
  const annotations = [
    {
      drawTime: 'afterDatasetsDraw',
      id: 'hline',
      type: 'line',
      mode: 'horizontal',
      scaleID: 'y-axis-0',
      value: wtdAverageValue,
      borderColor: barChartSettings.wtdAvgLineBarColor,
      borderWidth: 2,
      label: {
        fontSize: 16,
        fontColor: '#000',
        fontStyle: 'normal',
        fontFamily: FONT_FAMILY_MONTSERRAT,
        backgroundColor: '#fff',
        content: `Average ${float2dollar(wtdAverageValue, pageTitle)}`,
        enabled: true,
        position: 'left',
        yAdjust: isOverlapping(
          wtdAverageValue,
          barChartSettings,
          pageTitle,
          chartData
        )
          ? -45
          : -15,
        xAdjust: 8
      }
    }
  ]

  const chartOptions = {
    tooltips: {
      enabled: false
    },
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    annotation: {
      annotations
    },
    hover: {
      animationDuration: 0
    },
    layout: {
      padding: {
        left: 1,
        right: 1,
        top: 10,
        bottom: 1
      }
    }
  }

  return chartOptions
}

export const sortChartData = chartData => {
  const sortedChartData = []
  chartData.datasets[0].data.forEach((value, index) => {
    const obj = {}
    obj.label = chartData.labels[index]
    obj.bgColor = chartData.datasets[0].backgroundColor[index]
    obj.avgMR = value
    sortedChartData.push(obj)
  })

  sortedChartData.sort((obj1, obj2) => obj1.avgMR - obj2.avgMR)

  return sortedChartData
}

export const barDataOfUnitMixAvgPerUnitType = (
  propertyInfo,
  bedroomTypeExcludingBathType,
  barChartSettings
) => {
  const chartData = {
    labels: [],
    yaxisConfig: {},
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderWidth: 1
      }
    ]
  }
  const bedroomTypeArr = {}
  bedroomTypeArr[bedroomTypeExcludingBathType] = []

  propertyInfo.forEach(property => {
    if (property.propertyType === 'NA') {
      return
    }
    chartData.labels.push(property.name)
    if (property.propertyType.toString().includes('S')) {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.subjectPropBarColor
      )
    } else {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.compPropBarColor
      )
    }

    const compsWithSameBedroomTypeExcludingBathType = property.comparableData.filter(
      unitMixObj =>
        unitMixObj.unitMarketRent &&
        unitMixObj.unitMarketRent !== 'NA' &&
        unitMixObj.unitType.includes(bedroomTypeExcludingBathType)
    )

    let totalUnits = 0
    let totalSum = 0
    let totalAverageForProperty = 0
    compsWithSameBedroomTypeExcludingBathType.forEach(unitMixObj => {
      const units = Number(unitMixObj.units)
      totalUnits += units
      totalSum += units * unitMixObj.unitMarketRent
    })

    if (
      !property.propertyType.toString().includes('S') &&
      property.propertyType !== 'NA' &&
      totalSum !== 0 &&
      totalUnits !== 0
    ) {
      const wtdavg = Number(totalSum / totalUnits)
      if (bedroomTypeExcludingBathType in bedroomTypeArr) {
        bedroomTypeArr[bedroomTypeExcludingBathType].push({
          wtdavg,
          totalUnits
        })
      }
    }

    if (totalUnits === 0) {
      chartData.datasets[0].backgroundColor.pop()
      chartData.labels.pop()
    } else {
      totalAverageForProperty = totalSum / totalUnits
      chartData.datasets[0].data.push(totalAverageForProperty)
    }
  })

  const temp = sortChartData(chartData)
  chartData.labels = []
  chartData.datasets[0].data = []
  chartData.datasets[0].backgroundColor = []

  temp.forEach(obj => {
    chartData.labels.push(obj.label)
    chartData.datasets[0].data.push(obj.avgMR)
    chartData.datasets[0].backgroundColor.push(obj.bgColor)
  })
  const yAxisCongig = niceScale(
    Math.min.apply(this, chartData.datasets[0].data),
    Math.max.apply(this, chartData.datasets[0].data)
  )
  chartData.yaxisConfig.min = yAxisCongig.niceMinimum
  chartData.yaxisConfig.max = yAxisCongig.niceMaximum
  chartData.yaxisConfig.interval = yAxisCongig.tickSpacing

  return { chartData, bedroomTypeArr }
}

export const checkAtleastTwoPropertiesHaveSameBedType = (
  propertyInfo,
  bedroomTypeExcludingBathType
) => {
  let minCountProperties = 0
  for (let index = 0; index < propertyInfo.length; index += 1) {
    const property = propertyInfo[index]
    if (property.propertyType !== 'NA') {
      const compsWithSameBedroomTypeExcludingBathType = property.comparableData.filter(
        unitMixObj => unitMixObj.unitType.includes(bedroomTypeExcludingBathType)
      )
      if (compsWithSameBedroomTypeExcludingBathType.length > 0) {
        minCountProperties += 1
      }
      // break the loop if pre-condition is met
      if (minCountProperties > 1) {
        break
      }
    }
  }

  return minCountProperties > 1
}

export const checkAtleastTwoPropertiesHasSameType = (propertyInfo, key) => {
  const filteredProperties = propertyInfo.filter(
    property =>
      property.propertyType !== 'NA' && property[key] && property[key] !== 'NA'
  )

  return filteredProperties.length >= 2
}

export const weightedAverageExcludingSubject = (
  { bedroomTypeArr },
  bedroomTypeExcludingBathType
) => {
  let weightedSum = 0
  let tUnits = 0

  bedroomTypeArr[bedroomTypeExcludingBathType].forEach(currentValue => {
    if (currentValue.wtdavg !== null) {
      weightedSum += Number(
        Math.round(currentValue.wtdavg) * currentValue.totalUnits
      )
      tUnits += currentValue.totalUnits
    }
  })

  const wtdAverage = (weightedSum / tUnits).toFixed(2)

  return Number(wtdAverage)
}

/* eslint max-params: [1, 4] */
export const getAvgBarChart = (
  propertyInfo,
  compColumnKey,
  pageTitle,
  barChartSettings
) => {
  const chartData = {
    labels: [],
    yaxisConfig: {},
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderWidth: 1
      }
    ]
  }

  let wtdAverage = 0
  propertyInfo.forEach(property => {
    if (property.propertyType === 'NA') {
      wtdAverage = Number(property[compColumnKey]).toFixed(2)
      wtdAverage = Number(wtdAverage)

      return
    }
    if (!property[compColumnKey] || property[compColumnKey] === 'NA') {
      return
    }
    chartData.labels.push(property.name)
    let avgMarketRent = property[compColumnKey]
    avgMarketRent = Number(avgMarketRent)
    chartData.datasets[0].data.push(avgMarketRent)
    if (property.propertyType.toString().includes('S')) {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.subjectPropBarColor
      )
    } else {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.compPropBarColor
      )
    }
  })

  const temp = sortChartData(chartData)
  chartData.labels = []
  chartData.datasets[0].data = []
  chartData.datasets[0].backgroundColor = []

  temp.forEach(obj => {
    chartData.labels.push(obj.label)
    chartData.datasets[0].data.push(obj.avgMR)
    chartData.datasets[0].backgroundColor.push(obj.bgColor)
  })
  const yAxisCongig = niceScale(
    Math.min.apply(this, chartData.datasets[0].data),
    Math.max.apply(this, chartData.datasets[0].data)
  )
  chartData.yaxisConfig.min = yAxisCongig.niceMinimum
  chartData.yaxisConfig.max = yAxisCongig.niceMaximum
  chartData.yaxisConfig.interval = yAxisCongig.tickSpacing

  const chartObj = {
    pageType: compColumnKey,
    pageTitle,
    chartData,
    chartOptions: prepareChartOptions(
      wtdAverage,
      barChartSettings,
      pageTitle,
      chartData
    ),
    wtdAverage: {
      value: wtdAverage,
      lineColor: barChartSettings.wtdAvgLineBarColor
    }
  }

  return chartObj
}

export const getPageTitle = bedroomType => {
  const numberToWordMapping = {
    '0': 'Studio',
    '1': 'One Bedroom',
    '2': 'Two Bedroom',
    '3': 'Three Bedroom',
    '4': 'Four Bedroom',
    '5': 'Five Bedroom',
    '6': 'Six Bedroom',
    '7': 'Seven Bedroom',
    '8': 'Eight Bedroom',
    studio: 'Studio'
  }

  const pageTitle = numberToWordMapping[bedroomType.split(' ')[0]]

  return pageTitle || bedroomType
}

export const getChartData = (
  propertyInfo,
  templateName,
  barChartSettings = {}
) => {
  const barChartData = []
  if (Object.keys(barChartSettings).length === 0) {
    barChartSettings.subjectPropBarColor =
      BAR_CHART_COLOR_CODES.subjectPropBarColor
    barChartSettings.compPropBarColor = BAR_CHART_COLOR_CODES.compPropBarColor
    barChartSettings.wtdAvgLineBarColor =
      BAR_CHART_COLOR_CODES.wtdAvgLineBarColor
  }
  if (
    templateName?.includes('rentComps') ||
    templateName?.includes('studentHousingComps')
  ) {
    const bedroomTypes = []

    propertyInfo.forEach(property => {
      if (property.propertyType === 'NA') {
        return
      }
      // Reading bedroom types from Donut chartData object
      bedroomTypes.push(...Object.keys(property.chartData))
    })

    const filteredBedroomTypes = []
    bedroomTypes.forEach(bedroomType => {
      const bedroomTypeExcludingBathType = bedroomType.split('/')[0].trim()
      if (!filteredBedroomTypes.includes(bedroomTypeExcludingBathType)) {
        filteredBedroomTypes.push(bedroomTypeExcludingBathType)
      }
    })

    filteredBedroomTypes.sort((bedroom1, bedroom2) => {
      const [bedNum1] = bedroom1.split(' ')
      const [bedNum2] = bedroom2.split(' ')

      return bedNum1 - bedNum2
    })
    // Loop over #of bedroom types
    filteredBedroomTypes.forEach(bedroomTypeExcludingBathType => {
      let chartDataPerBedroomType = null
      if (
        checkAtleastTwoPropertiesHaveSameBedType(
          propertyInfo,
          bedroomTypeExcludingBathType
        )
      ) {
        chartDataPerBedroomType = barDataOfUnitMixAvgPerUnitType(
          propertyInfo,
          bedroomTypeExcludingBathType,
          barChartSettings
        )
      }
      if (chartDataPerBedroomType && chartDataPerBedroomType.chartData) {
        const wtdAverageValue = weightedAverageExcludingSubject(
          chartDataPerBedroomType,
          bedroomTypeExcludingBathType
        )
        const chartObj = {
          pageType: 'bedroom',
          chartData: chartDataPerBedroomType.chartData,
          chartOptions: prepareChartOptions(
            wtdAverageValue,
            barChartSettings,
            getPageTitle(bedroomTypeExcludingBathType),
            chartDataPerBedroomType.chartData
          ),
          pageTitle: getPageTitle(bedroomTypeExcludingBathType),
          wtdAverage: {
            value: wtdAverageValue,
            lineColor: barChartSettings.wtdAvgLineBarColor
          }
        }
        barChartData.push(chartObj)
      }
    })

    const avgRentBarChart = getAvgBarChart(
      propertyInfo,
      'avgMarketRentSf',
      'Average Rent Summary',
      barChartSettings
    )
    const avgRentSFBarChart = getAvgBarChart(
      propertyInfo,
      'avgRentSf',
      'Average Rent/SF Summary',
      barChartSettings
    )
    barChartData.push(avgRentBarChart)
    barChartData.push(avgRentSFBarChart)
  }
  if (templateName === 'salesComps') {
    const PricePerUnit = 'Price/Unit'
    const PricePerSF = 'Price/SF'
    const CAP = 'CAP'
    if (checkAtleastTwoPropertiesHasSameType(propertyInfo, 'priceUnit')) {
      const avgPricePerUnitBarChart = getAvgBarChart(
        propertyInfo,
        'priceUnit',
        PricePerUnit,
        barChartSettings
      )
      barChartData.push(avgPricePerUnitBarChart)
    }
    if (checkAtleastTwoPropertiesHasSameType(propertyInfo, 'priceSf')) {
      const avgPricePerSFBarChart = getAvgBarChart(
        propertyInfo,
        'priceSf',
        PricePerSF,
        barChartSettings
      )
      barChartData.push(avgPricePerSFBarChart)
    }
    if (checkAtleastTwoPropertiesHasSameType(propertyInfo, 'capRate')) {
      const avgCAPBarChart = getAvgBarChart(
        propertyInfo,
        'capRate',
        CAP,
        barChartSettings
      )
      barChartData.push(avgCAPBarChart)
    }
  }

  return barChartData
}

export const changeBarColors = (
  barChartData,
  barChartSettings,
  subjectPropName
) => {
  barChartData.map(barChartObj => {
    barChartObj.wtdAverage.lineColor = barChartSettings.wtdAvgLineBarColor
    barChartObj.chartOptions.annotation.annotations[0].borderColor =
      barChartSettings.wtdAvgLineBarColor
    const findSubjectIndexes = barChartObj.chartData.labels.reduce(
      (values, el, i) => {
        if (subjectPropName.includes(el)) {
          values.push(i)
        }

        return values
      },
      []
    )

    barChartObj.chartData.datasets[0].backgroundColor.map((a, i) => {
      barChartObj.chartData.datasets[0].backgroundColor[i] =
        barChartSettings.compPropBarColor

      const findSubjectIndexesIterator = findSubjectIndexes.values()
      for (const value of findSubjectIndexesIterator) {
        if (i === value) {
          barChartObj.chartData.datasets[0].backgroundColor[i] =
            barChartSettings.subjectPropBarColor
        }
      }

      return null
    })

    return barChartObj
  })

  return barChartData
}

export function niceScale(min, max) {
  minPoint = min
  maxPoint = max
  calculate()

  return {
    tickSpacing: Number(tickSpacing.toFixed(2)),
    niceMinimum: Number(niceMin.toFixed(2)),
    niceMaximum: Number(niceMax.toFixed(2))
  }
}

function calculate() {
  range = niceNum(maxPoint - minPoint, false)
  tickSpacing = niceNum(range / (maxTicks - 1), true)
  niceMin = Math.floor(minPoint / tickSpacing) * tickSpacing
  niceMax = Math.ceil(maxPoint / tickSpacing) * tickSpacing
}

function niceNum(localRange, round) {
  let exponent = 0
  let fraction = 0
  let niceFraction = 0

  exponent = Math.floor(Math.log10(localRange))
  fraction = localRange / Math.pow(10, exponent)

  if (round) {
    if (fraction < 1.5) {
      niceFraction = 1
    } else if (fraction < 3) {
      niceFraction = 2
    } else if (fraction < 7) {
      niceFraction = 5
    } else {
      niceFraction = 10
    }
  } else if (fraction <= 1) {
    niceFraction = 1
  } else if (fraction <= 2) {
    niceFraction = 2
  } else if (fraction <= 5) {
    niceFraction = 5
  } else {
    niceFraction = 10
  }

  return niceFraction * Math.pow(10, exponent)
}

export const getFpData = propertyData => {
  let summaryArr = []

  propertyData.forEach(attr => {
    const unitTypeArr = groupBy(
      attr.comparableData,
      u => u.unitType.split('/')[0]
    )

    Object.keys(unitTypeArr).forEach(key => {
      const fpObj = {}
      fpObj.name = attr.name
      fpObj.propertyType = attr.propertyType
      fpObj.yearBuilt = attr.yearBuilt
      fpObj.yearRen = attr.yearRen
      fpObj.avgMarketRentSf = attr.avgMarketRentSf
      fpObj.avgRentSf = attr.avgRentSf
      fpObj.avgUnitSize = attr.unitSize
      fpObj.unitEffectiveRent = attr.unitEffectiveRent
      fpObj.erPerSf = attr.erPerSf
      fpObj.builtPerRenovated = getCustomDisplayValue(
        attr.yearBuilt,
        attr.yearRen
      )

      const summaryObj = {
        type: '',
        pageTitle: '',
        summary: []
      }

      if (summaryArr.some(sObj => sObj.type.includes(key))) {
        const existingObj = summaryArr.find(dataObj =>
          dataObj.type.includes(key)
        )
        fpObj.units = sumUnits(unitTypeArr[key], 'units')
        fpObj.avgUnitSize = roundOff(
          sumUnits(unitTypeArr[key], 'totalSize') / fpObj.units,
          0
        )
        fpObj.avgMarketRentSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'unitMarketRent') / fpObj.units,
          0
        )
        fpObj.avgRentSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'mrPerSf') / fpObj.units,
          2
        )
        fpObj.unitEffectiveRent = roundOff(
          getAvgMrsf(unitTypeArr[key], 'unitEffectiveRent') / fpObj.units,
          0
        )
        fpObj.erPerSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'erPerSf') / fpObj.units,
          2
        )
        existingObj.summary.push(fpObj)
      } else if (key !== 'NA') {
        summaryObj.type = key
        summaryObj.pageTitle = `${getPageTitle(key)} Summary`
        fpObj.units = sumUnits(unitTypeArr[key], 'units')
        fpObj.avgUnitSize = roundOff(
          sumUnits(unitTypeArr[key], 'totalSize') / fpObj.units,
          0
        )
        fpObj.avgMarketRentSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'unitMarketRent') / fpObj.units,
          0
        )
        fpObj.avgRentSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'mrPerSf') / fpObj.units,
          2
        )
        fpObj.unitEffectiveRent = roundOff(
          getAvgMrsf(unitTypeArr[key], 'unitEffectiveRent') / fpObj.units,
          0
        )
        fpObj.erPerSf = roundOff(
          getAvgMrsf(unitTypeArr[key], 'erPerSf') / fpObj.units,
          2
        )
        summaryObj.summary.push(fpObj)
        summaryArr.push(summaryObj)
      }
    })
  })

  summaryArr = summaryArr.filter(obj => obj.summary.length > 1)
  summaryArr.sort(
    (a, b) => Number(a.type.split(' ')[0]) - Number(b.type.split(' ')[0])
  )
  summaryArr.forEach(fp => {
    const newFpObj = {}
    let comparableIndex = 0
    fp.summary.forEach((sumObj, index) => {
      if (fp.summary[index].propertyType.toString().includes('S')) {
        sumObj.propertyType = fp.summary[index].propertyType
      } else {
        comparableIndex += 1
        sumObj.propertyType = comparableIndex
      }
    })
    fp.type = fp.type.includes('0') ? 'studio' : fp.type.trim().toLowerCase()
    fp.summary.sort((a, b) => a.propertyType - b.propertyType)
    if (fp.summary.length >= 2) {
      newFpObj.propertyType = 'NA'
      newFpObj.name = 'Total / Averages'
      newFpObj.units = getFPTotalUnits(fp.summary, 'units')
      newFpObj.yearBuilt = null
      newFpObj.yearRen = null
      newFpObj.avgUnitSize = getWeightedAvg(fp.summary, 'avgUnitSize', 0)
      newFpObj.avgMarketRentSf = getWeightedAvg(
        fp.summary,
        'avgMarketRentSf',
        0
      )
      newFpObj.avgRentSf = Number(
        (newFpObj.avgMarketRentSf / newFpObj.avgUnitSize).toFixed(2)
      )
      newFpObj.unitEffectiveRent = getWeightedAvg(
        fp.summary,
        'unitEffectiveRent',
        0
      )
      newFpObj.erPerSf = getWeightedAvg(fp.summary, 'erPerSf', 2)
      newFpObj.builtPerRenovated = 'NA'

      fp.summary.push(newFpObj)
    }
  })

  return summaryArr
}

const sumUnits = (arr, key) => arr.reduce((prev, cur) => prev + cur[key], 0)
const getFPTotalUnits = (arr, key) =>
  arr.reduce((prev, cur) => {
    if (!cur.propertyType.toString().includes('S')) {
      return prev + cur[key]
    }

    return prev
  }, 0)

const getAvgMrsf = (arr, key) =>
  arr.reduce((prev, cur) => {
    if (cur[key] && cur[key] !== 'NA') {
      return prev + (cur.units * cur[key])
    }

    return prev
  }, 0)

const getWeightedAvg = (values, key, roundOff) => {
  let weightedSum = 0
  let totalUnits = 0

  values.forEach(currentValue => {
    if (!currentValue.propertyType.toString().includes('S')) {
      if (currentValue[key] !== null) {
        weightedSum += currentValue[key] * currentValue.units
        totalUnits += currentValue.units
      }
    }
  })

  return Number((weightedSum / totalUnits).toFixed(roundOff))
}

const roundOff = (value, toroundoff) =>
  (value === 0 ? null : Number(value.toFixed(toroundoff)))
const groupBy = (arr, criteria) =>
  arr.reduce((obj, item) => {
    const key = typeof criteria === 'function' ? criteria(item) : item[criteria]
    if (!obj.hasOwnProperty(key)) {
      obj[key] = []
    }
    obj[key].push(item)

    return obj
  }, {})

const getCustomDisplayValue = (val1, val2) =>
  (val1 && val2 ? `${val1} / ${val2}` : val1 ? val1 : val2 ? val2 : 'NA')

export const getFPBarChartData = (fpSummaryData, barChartSettings = {}) => {
  const fpBarChartData = []
  fpSummaryData.forEach(summaryObj => {
    if (summaryObj.summary.length >= 2) {
      const fpBarChartAvgRent = generateFPBarChart(
        summaryObj,
        'avgMarketRentSf',
        'Avg Rent',
        barChartSettings
      )
      fpBarChartData.push(fpBarChartAvgRent)

      const fpBarChartAvgRentSF = generateFPBarChart(
        summaryObj,
        'avgRentSf',
        'Avg Rent/SF',
        barChartSettings
      )
      fpBarChartData.push(fpBarChartAvgRentSF)
    }
  })

  return fpBarChartData
}

export const generateFPBarChart = (
  summaryObj,
  compColumnKey,
  pageTitle,
  barChartSettings
) => {
  if (Object.keys(barChartSettings).length === 0) {
    barChartSettings.subjectPropBarColor =
      BAR_CHART_COLOR_CODES.subjectPropBarColor
    barChartSettings.compPropBarColor = BAR_CHART_COLOR_CODES.compPropBarColor
    barChartSettings.wtdAvgLineBarColor =
      BAR_CHART_COLOR_CODES.wtdAvgLineBarColor
  }
  const chartData = {
    labels: [],
    yaxisConfig: {},
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderWidth: 1
      }
    ]
  }

  let wtdAverage = 0
  summaryObj.summary.forEach(property => {
    if (property.propertyType === 'NA') {
      wtdAverage = Number(property[compColumnKey]).toFixed(2)
      wtdAverage = Number(wtdAverage)

      return
    }
    chartData.labels.push(property.name)
    let avgMarketRent = property[compColumnKey]
    avgMarketRent = Number(avgMarketRent)
    chartData.datasets[0].data.push(avgMarketRent)
    if (property.propertyType.toString().includes('S')) {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.subjectPropBarColor
      )
    } else {
      chartData.datasets[0].backgroundColor.push(
        barChartSettings.compPropBarColor
      )
    }
  })

  const temp = sortChartData(chartData)
  chartData.labels = []
  chartData.datasets[0].data = []
  chartData.datasets[0].backgroundColor = []

  temp.forEach(obj => {
    chartData.labels.push(obj.label)
    chartData.datasets[0].data.push(obj.avgMR)
    chartData.datasets[0].backgroundColor.push(obj.bgColor)
  })
  const yAxisCongig = niceScale(
    Math.min.apply(this, chartData.datasets[0].data),
    Math.max.apply(this, chartData.datasets[0].data)
  )
  chartData.yaxisConfig.min = yAxisCongig.niceMinimum
  chartData.yaxisConfig.max = yAxisCongig.niceMaximum
  chartData.yaxisConfig.interval = yAxisCongig.tickSpacing

  const chartObj = {
    type: summaryObj.type,
    pageType: compColumnKey,
    pageTitle: `${pageTitle} - ${getPageTitle(summaryObj.type)}`,
    chartData,
    chartOptions: prepareChartOptions(
      wtdAverage,
      barChartSettings,
      pageTitle,
      chartData
    ),
    wtdAverage: {
      value: wtdAverage,
      lineColor: barChartSettings.wtdAvgLineBarColor
    }
  }

  return chartObj
}
